// FILE: components/shared/UserHoverCard.tsx
"use client";

import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { UserPlus, UserCheck, Award, Copy, CheckCircle2, MessageSquare } from "lucide-react";
import { createClient } from "@/utils/supabase/client";

interface UserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  level: number;
  is_expert: boolean;
  bio?: string | null;
  referral_code?: string | null;
  diamonds?: number;
}

interface Props {
  userId: string;
  name: string;
  className?: string;
  onNavigate?: (page: string) => void;
}

export default function UserHoverCard({ userId, name, className, onNavigate }: Props) {
  const [hover, setHover] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [following, setFollowing] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, above: false });
  const ref = useRef<HTMLSpanElement>(null);
  const enterTimer = useRef<ReturnType<typeof setTimeout>>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(null);

  const fetchProfile = useCallback(async () => {
    if (fetched || loading || !userId) return;
    setLoading(true);
    try {
      const supabase = createClient();
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}/.test(userId);
      const { data } = await supabase
        .from("users")
        .select("id, full_name, username, avatar_url, referral_code, diamonds")
        .eq(isUUID ? "id" : "username", userId)
        .single();
      if (data) setProfile(data as UserProfile);
    } catch {}
    setLoading(false);
    setFetched(true);
  }, [userId, fetched, loading]);

  function handleEnter() {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const above = spaceBelow < 320;
        setPos({
          top: above ? rect.top - 8 : rect.bottom + 8,
          left: Math.max(12, Math.min(rect.left - 20, window.innerWidth - 330)),
          above,
        });
      }
      setHover(true);
      fetchProfile();
    }, 150);
  }

  function handleLeave() {
    if (enterTimer.current) clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => setHover(false), 200);
  }

  function handleClick() {
    window.location.href = `/${userId}`;
  }

  function copyRefCode() {
    if (!profile?.referral_code) return;
    navigator.clipboard.writeText(profile.referral_code);
    setCopiedRef(true);
    setTimeout(() => setCopiedRef(false), 1500);
  }

  const initials = (name || "U").slice(0, 1).toUpperCase();
  const displayName = profile?.full_name ?? name;
  const uname = profile?.username;

  return (
    <>
      <span ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClick(); }}
        className={`font-semibold text-teal-800 hover:text-orange-500 hover:underline cursor-pointer transition-colors ${className ?? "text-[12px]"}`}>
        {name}
      </span>

      {hover && typeof document !== "undefined" && createPortal(
        <div className="fixed z-[80]"
          style={{
            top: pos.above ? undefined : pos.top,
            bottom: pos.above ? `${window.innerHeight - pos.top}px` : undefined,
            left: pos.left,
          }}
          onMouseEnter={() => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }}
          onMouseLeave={handleLeave}>

          <div className="w-[300px] bg-white border border-border rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,.15)] overflow-hidden">

            {/* Skeleton Loading */}
            {loading && !profile && (
              <div className="animate-pulse">
                <div className="h-[60px] bg-gradient-to-r from-teal-100 to-teal-50" />
                <div className="px-4 pb-4">
                  <div className="w-14 h-14 rounded-full bg-slate-200 -mt-7 border-[3px] border-white" />
                  <div className="mt-2 space-y-2">
                    <div className="h-4 w-32 bg-slate-100 rounded" />
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                    <div className="h-3 w-full bg-slate-100 rounded" />
                    <div className="h-8 w-full bg-slate-100 rounded-lg mt-3" />
                  </div>
                </div>
              </div>
            )}

            {/* Loaded Content */}
            {profile && (
              <>
                {/* Header — Gradient cover */}
                <div className="h-[60px] bg-gradient-to-r from-[#0f3a4a] via-[#1a5570] to-[#2a8aaa] relative">
                  <div className="absolute -bottom-6 left-4">
                    {profile.avatar_url ? (
                      <img src={profile.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border-[3px] border-white shadow-md" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-teal-600 text-white flex items-center justify-center text-[18px] font-bold border-[3px] border-white shadow-md">
                        {initials}
                      </div>
                    )}
                  </div>
                </div>

                {/* Body */}
                <div className="px-4 pt-8 pb-4">
                  {/* Name + Level */}
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span onClick={handleClick} className="text-[14px] font-extrabold text-foreground hover:text-teal-700 cursor-pointer transition-colors">
                      {displayName}
                    </span>
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[8px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                      <Award className="w-2.5 h-2.5" />Lv.{profile.level ?? 1}
                    </span>
                    {profile.is_expert && (
                      <span className="inline-flex items-center px-1.5 py-[1px] rounded-full text-[8px] font-bold bg-[#f5f3ff] text-[#7c3aed] border border-[#ddd6fe]">
                        👑 Chuyên gia
                      </span>
                    )}
                  </div>

                  {/* Username + Ref ID */}
                  <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-2">
                    {uname && <span>@{uname}</span>}
                    {uname && profile.referral_code && <span>•</span>}
                    {profile.referral_code && (
                      <button onClick={(e) => { e.stopPropagation(); copyRefCode(); }}
                        className="inline-flex items-center gap-0.5 hover:text-teal-600 transition-colors" title="Copy mã giới thiệu">
                        Ref: {profile.referral_code}
                        {copiedRef ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> : <Copy className="w-2.5 h-2.5" />}
                      </button>
                    )}
                  </div>

                  {/* Bio */}
                  {profile.bio && (
                    <p className="text-[11px] text-foreground/70 leading-relaxed line-clamp-2 mb-2.5">{profile.bio}</p>
                  )}

                  {/* Diamonds */}
                  {(profile.diamonds ?? 0) > 0 && (
                    <div className="text-[10px] text-muted-foreground mb-3">
                      💎 {(profile.diamonds ?? 0).toLocaleString()} Kim cương
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button onClick={(e) => { e.stopPropagation(); setFollowing(p => !p); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-bold transition-all ${
                        following
                          ? "bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
                          : "bg-teal-800 text-white hover:bg-teal-700 shadow-sm"
                      }`}>
                      {following ? <><UserCheck className="w-3 h-3" />Đang theo dõi</> : <><UserPlus className="w-3 h-3" />Theo dõi</>}
                    </button>
                    <button onClick={handleClick}
                      className="px-3 py-2 rounded-xl border border-border text-[11px] font-semibold text-muted-foreground hover:bg-muted transition-colors shrink-0">
                      Xem trang
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Error — only show after fetch complete with no result */}
            {fetched && !loading && !profile && (
              <div className="p-6 text-center">
                <p className="text-[12px] text-muted-foreground">Không tìm thấy người dùng</p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}